import React, { useState, useEffect, useRef } from 'react';
import StoreButtons from '../components/common/StoreButtons';
import { resolveImageUrl } from '../profile/hooks/useProfileImage';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { QRCodeSVG } from 'qrcode.react';
import { useCountUp } from '../hooks/useCountUp';
import {
  Trophy, Users, Zap, Target, Star, ChevronRight,
  Play, Shield, Award, TrendingUp, Globe, MessageSquare, Volume2, VolumeX,
  Copy, Mail, Share2, Check
} from 'lucide-react';

const SPORTS = [
  { emoji: '🏀', name: 'Basketball', color: '#f97316' },
  { emoji: '🏐', name: 'Volleyball', color: '#3b82f6' },
  { emoji: '⚽', name: 'Soccer', color: '#22c55e' },
  { emoji: '🎾', name: 'Tennis', color: '#eab308' },
  { emoji: '🏸', name: 'Pickleball', color: '#a855f7' },
  { emoji: '🏏', name: 'Cricket', color: '#06b6d4' },
  { emoji: '🏓', name: 'Ping Pong', color: '#f43f5e' },
  { emoji: '🏸', name: 'Badminton', color: '#64748b' },
];


const FEATURES = [
  {
    icon: Trophy,
    title: 'Run Tournaments',
    desc: 'Create and manage full bracket tournaments, round robins, and leagues with live scoring.',
    color: '#f97316',
    glow: 'rgba(249,115,22,0.3)',
  },
  {
    icon: Users,
    title: 'Build Squads',
    desc: 'Form your crew, chat in real-time, track expenses, and schedule games together.',
    color: '#a855f7',
    glow: 'rgba(168,85,247,0.3)',
  },
  {
    icon: Zap,
    title: 'Live Scoring',
    desc: 'Track every point, set, and game in real-time. Basketball, tennis, cricket and more.',
    color: '#3b82f6',
    glow: 'rgba(59,130,246,0.3)',
  },
  {
    icon: Target,
    title: 'Find Players',
    desc: 'Discover teammates near you. Connect, challenge, and grow your sports network.',
    color: '#22c55e',
    glow: 'rgba(34,197,94,0.3)',
  },
  {
    icon: TrendingUp,
    title: 'Track Stats',
    desc: 'Full performance analytics. Win rates, rankings, personal bests, tournament history.',
    color: '#eab308',
    glow: 'rgba(234,179,8,0.3)',
  },
  {
    icon: Globe,
    title: 'Marketplace',
    desc: 'Buy and sell sports gear in our community marketplace. Find deals, list your equipment.',
    color: '#f43f5e',
    glow: 'rgba(244,63,94,0.3)',
  },
];

function FloatingEmoji({ emoji, style }) {
  return (
    <div className="absolute text-5xl select-none pointer-events-none" style={{ ...style, animation: style.animation }}>
      {emoji}
    </div>
  );
}

export default function LandingV1Page() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { theme, currentTheme } = useTheme();

  const isDark = ['dark', 'darkGreen', 'darkBlue', 'darkPurple', 'darkOrange'].includes(currentTheme);
  const cardBg = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)';
  const cardBorder = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.1)';
  const textMuted = isDark ? '#9ca3af' : '#6b7280';
  const textFaint = isDark ? '#6b7280' : '#9ca3af';
  const overlayBg = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)';
  const overlayBorder = isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)';
  const gradientText = isDark
    ? 'linear-gradient(135deg, #fff 0%, #a78bfa 40%, #60a5fa 70%, #34d399 100%)'
    : 'linear-gradient(135deg, #1a1a2e 0%, #7c3aed 40%, #2563eb 70%, #059669 100%)';
  const [activeSport, setActiveSport] = useState(0);
  const videoRef = useRef(null);
  const [isMuted, setIsMuted] = useState(true);
  const [videoError, setVideoError] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showGetApp, setShowGetApp] = useState(false);
  const [modalEmail, setModalEmail] = useState('');
  const [modalSending, setModalSending] = useState(false);
  const [modalSent, setModalSent] = useState(false);
  const [modalCopied, setModalCopied] = useState(false);

  const APP_URL = 'https://www.kynaixsports.com/';

  function handleCopyLink() {
    navigator.clipboard.writeText(APP_URL).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  function handleEmailShare() {
    setShowGetApp(true);
  }

  async function handleNativeShare() {
    // No `text` field — Android has no separate URL slot in its share intent, so
    // text + url get concatenated into one string, which breaks the share sheet's
    // "Copy" action (copies "blurb https://..." instead of a clean, pasteable link).
    try {
      await navigator.share({ title: 'Kynaix Sports', url: APP_URL });
    } catch (_) { /* cancelled or not supported */ }
  }

  async function handleModalSend() {
    if (!modalEmail || !modalEmail.includes('@')) return;
    setModalSending(true);
    try {
      const res = await fetch('/exportsapi/share/send-app-link', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: modalEmail }),
      });
      const data = await res.json();
      if (data.success) { setModalSent(true); setModalEmail(''); }
    } catch (_) {}
    setModalSending(false);
  }

  function handleModalCopy() {
    navigator.clipboard.writeText(APP_URL).then(() => {
      setModalCopied(true);
      setTimeout(() => setModalCopied(false), 2200);
    });
  }

  useEffect(() => {
    const t = setInterval(() => setActiveSport(s => (s + 1) % SPORTS.length), 1800);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!showGetApp) return;
    const onKey = (e) => { if (e.key === 'Escape') setShowGetApp(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [showGetApp]);

  const floatingEmojis = [
    { emoji: '🏀', style: { top: '12%', left: '8%', opacity: 0.12, fontSize: '4rem', animation: 'float1 6s ease-in-out infinite' } },
    { emoji: '⚽', style: { top: '25%', right: '6%', opacity: 0.10, fontSize: '5rem', animation: 'float2 8s ease-in-out infinite' } },
    { emoji: '🎾', style: { top: '60%', left: '4%', opacity: 0.10, fontSize: '3.5rem', animation: 'float3 7s ease-in-out infinite' } },
    { emoji: '🏆', style: { top: '70%', right: '8%', opacity: 0.12, fontSize: '4rem', animation: 'float1 9s ease-in-out infinite reverse' } },
    { emoji: '🏸', style: { top: '45%', left: '12%', opacity: 0.08, fontSize: '3rem', animation: 'float2 10s ease-in-out infinite' } },
    { emoji: '🏏', style: { top: '15%', right: '20%', opacity: 0.08, fontSize: '3rem', animation: 'float3 5.5s ease-in-out infinite' } },
  ];

  return (
    <div className={`${theme.background} ${theme.text}`} style={{ minHeight: '100vh', fontFamily: 'Inter, system-ui, sans-serif', overflowX: 'hidden' }}>
      {/* Title, description and the og:/twitter: pairs used to be duplicated here.
          index.html already ships all of them, and Helmet appends rather than
          replaces, so the homepage was serving two descriptions and two og:url
          tags. RouteSeo now owns those (including this page's punchier social
          copy, passed as ogTitle/ogDescription). Only the JSON-LD stays: there is
          no competing script tag in the template, so it cannot duplicate.

          The Organization and WebSite nodes used to be here too, and index.html
          now defines both with the same @ids — so the homepage carried TWO
          definitions of #organization with different sameAs lists, which is the
          duplicate-canonical problem again in a different field. index.html wins
          because a crawler sees it before any JS runs. Only MobileApplication is
          left: it is the one node the template does not have, and its publisher
          reference resolves to the definition there. */}
      <Helmet>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "MobileApplication",
              "@id": "https://www.kynaixsports.com/#mobileapp",
              "name": "Kynaix Sports",
              "operatingSystem": ["iOS", "Android"],
              "applicationCategory": "SportsApplication",
              "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD" },
              "installUrl": [
                "https://apps.apple.com/us/app/kynaix-sports/id6761959059",
                "https://play.google.com/store/apps/details?id=com.kynaixsports"
              ],
              "description": "All-in-one sports community platform. Run tournaments, build squads, track live scores, find players, and buy or sell sports gear.",
              "publisher": { "@id": "https://www.kynaixsports.com/#organization" }
            }
          ]
        })}</script>
      </Helmet>
      <style>{`
        @keyframes float1 { 0%,100%{transform:translateY(0) rotate(0deg)} 50%{transform:translateY(-24px) rotate(8deg)} }
        @keyframes float2 { 0%,100%{transform:translateY(0) rotate(0deg)} 50%{transform:translateY(20px) rotate(-6deg)} }
        @keyframes float3 { 0%,100%{transform:translateY(0) rotate(0deg)} 33%{transform:translateY(-16px) rotate(4deg)} 66%{transform:translateY(10px) rotate(-4deg)} }
        @keyframes fadeInUp { from{opacity:0;transform:translateY(32px)} to{opacity:1;transform:translateY(0)} }
        @keyframes gradientShift { 0%{background-position:0% 50%} 50%{background-position:100% 50%} 100%{background-position:0% 50%} }
        @keyframes pulse-glow { 0%,100%{box-shadow:0 0 20px rgba(99,102,241,0.4)} 50%{box-shadow:0 0 50px rgba(99,102,241,0.8)} }
        .gradient-text {
          background: ${gradientText};
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .hero-glow { background: radial-gradient(ellipse 80% 50% at 50% 0%, rgba(99,102,241,0.25) 0%, transparent 70%); }
        .card-hover { transition: transform 0.3s ease, box-shadow 0.3s ease; }
        .card-hover:hover { transform: translateY(-6px); }
        .sport-card { transition: all 0.3s ease; cursor: pointer; }
        .sport-card:hover { transform: scale(1.08); }
        .animate-fade-in { animation: fadeInUp 0.8s ease forwards; }
      `}</style>

      {/* === HERO === */}
      <section className="relative flex flex-col justify-start items-center text-center px-4 sm:px-6 pt-8 pb-12 md:pb-16">
        <div className="hero-glow absolute inset-0 pointer-events-none" />
        {floatingEmojis.map((f, i) => <FloatingEmoji key={i} {...f} />)}

        {/* Kynaix Sports Name */}
        <div className="flex flex-col items-center gap-1 mb-3 animate-fade-in" style={{ animationDelay: '0s', opacity: 0 }}>
          <span className="text-xl font-black tracking-wide"
            style={{ background: 'linear-gradient(90deg, #2196F3, #9C4DCC, #FF9800)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            Kynaix Sports
          </span>
        </div>

        {/* Ad Video */}
        {!videoError && (
        <div className="w-full max-w-2xl mx-auto mb-4 animate-fade-in" style={{ animationDelay: '0.1s', opacity: 0 }}>
          <div className="relative rounded-2xl overflow-hidden"
            style={{ boxShadow: '0 0 60px rgba(99,102,241,0.3), 0 0 120px rgba(139,92,246,0.15)', border: '1px solid rgba(99,102,241,0.3)' }}>
            <video
              ref={videoRef}
              src={resolveImageUrl('/images/app_assets/Initial%20Ad.mp4')}
              autoPlay
              muted
              loop
              playsInline
              className="w-full block"
              style={{ borderRadius: '1rem' }}
              onError={() => setVideoError(true)}
            />
            <button
              onClick={() => {
                const v = videoRef.current;
                if (!v) return;
                v.muted = !v.muted;
                setIsMuted(v.muted);
              }}
              className="absolute bottom-3 right-3 flex items-center justify-center w-9 h-9 rounded-full transition-all"
              style={{ background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(6px)', border: '1px solid rgba(255,255,255,0.2)' }}
              title={isMuted ? 'Unmute' : 'Mute'}>
              {isMuted
                ? <VolumeX size={18} color="#fff" />
                : <Volume2 size={18} color="#fff" />}
            </button>
          </div>
        </div>
        )}

        {/* Main headline */}
        <h1 className="font-black leading-tight mb-3 animate-fade-in"
          style={{ fontSize: 'clamp(1.5rem, 4vw, 3rem)', animationDelay: '0.2s', opacity: 0 }}>
          <span className="gradient-text">YOUR GAME. </span>
          <span className={theme.text}>YOUR SQUAD. </span>
          <span className="gradient-text">YOUR GLORY.</span>
        </h1>

        <p className={`${theme.textMuted} max-w-xl mx-auto text-sm md:text-base leading-relaxed mb-6 animate-fade-in`}
          style={{ animationDelay: '0.4s', opacity: 0 }}>
          The all-in-one sports platform where athletes compete, connect, and conquer.
          Tournaments, live scoring, squads, and your entire sports life — in one place.
        </p>

        {/* CTAs */}
        <div className="flex flex-row gap-3 mb-8 animate-fade-in" style={{ animationDelay: '0.6s', opacity: 0 }}>
          {isAuthenticated ? (
            <>
              <button
                onClick={() => navigate('/tournaments')}
                className="px-8 py-3 rounded-2xl font-bold text-base transition-all"
                style={{
                  background: 'linear-gradient(135deg, #6366f1, #8b5cf6, #3b82f6)',
                  backgroundSize: '200% 200%',
                  animation: 'gradientShift 3s ease infinite, pulse-glow 2s ease infinite',
                  color: '#fff',
                  boxShadow: '0 0 40px rgba(99,102,241,0.5)',
                }}>
                Explore Tournaments →
              </button>
              <button
                onClick={() => navigate('/squads')}
                className="px-8 py-3 rounded-2xl font-bold text-base transition-all"
                style={{ background: cardBg, border: `1px solid ${overlayBorder}` }}>
                Browse Squads
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => navigate('/signin')}
                className="px-8 py-3 rounded-2xl font-bold text-base transition-all"
                style={{
                  background: 'linear-gradient(135deg, #6366f1, #8b5cf6, #3b82f6)',
                  backgroundSize: '200% 200%',
                  animation: 'gradientShift 3s ease infinite, pulse-glow 2s ease infinite',
                  color: '#fff',
                  boxShadow: '0 0 40px rgba(99,102,241,0.5)',
                }}>
                Sign In →
              </button>
              <button
                onClick={() => navigate('/signup')}
                className="px-8 py-3 rounded-2xl font-bold text-base transition-all"
                style={{ background: cardBg, border: `1px solid ${overlayBorder}` }}>
                Sign Up
              </button>
            </>
          )}
        </div>

        {/* App store badges — mobile only */}
        <div className="md:hidden flex flex-col items-center gap-2 mt-2 mb-6">
          <p className="text-xs opacity-50">Or download the app</p>
          <StoreButtons size="sm" />
        </div>

        {/* QR + Share — desktop only */}
        <div className="hidden md:flex items-center gap-5 mt-2 mb-6 px-5 py-4 rounded-2xl animate-fade-in"
          style={{ animationDelay: '0.7s', opacity: 0, background: cardBg, border: `1px solid ${cardBorder}` }}>
          {/* QR code */}
          <div className="flex-shrink-0 p-2 rounded-xl bg-white">
            <QRCodeSVG
              value={APP_URL}
              size={80}
              bgColor="#ffffff"
              fgColor="#1a1a2e"
              level="M"
            />
          </div>
          {/* Text + actions */}
          <div className="flex flex-col gap-2 min-w-0">
            <div>
              <p className={`font-bold text-sm ${theme.text}`}>Get the app on your phone</p>
              <p className={`text-xs ${theme.textMuted} mt-0.5`}>Scan the QR code or send the link to yourself</p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={handleCopyLink}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all"
                style={{ background: copied ? 'rgba(34,197,94,0.15)' : overlayBg, border: `1px solid ${copied ? 'rgba(34,197,94,0.4)' : overlayBorder}`, color: copied ? '#4ade80' : textMuted }}>
                {copied ? <Check size={13} /> : <Copy size={13} />}
                {copied ? 'Copied!' : 'Copy link'}
              </button>
              <button
                onClick={handleEmailShare}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all"
                style={{ background: overlayBg, border: `1px solid ${overlayBorder}`, color: textMuted }}>
                <Mail size={13} />
                Email to me
              </button>
              {typeof navigator !== 'undefined' && navigator.share && (
                <button
                  onClick={handleNativeShare}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all"
                  style={{ background: overlayBg, border: `1px solid ${overlayBorder}`, color: textMuted }}>
                  <Share2 size={13} />
                  Share
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Sport cycle */}
        <div className="flex flex-wrap justify-center gap-3 animate-fade-in" style={{ animationDelay: '0.8s', opacity: 0 }}>
          {SPORTS.map((s, i) => (
            <div
              key={s.name}
              className="sport-card flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold"
              style={{
                background: i === activeSport ? s.color + '25' : cardBg,
                border: `1px solid ${i === activeSport ? s.color : cardBorder}`,
                color: i === activeSport ? s.color : textMuted,
                boxShadow: i === activeSport ? `0 0 16px ${s.color}40` : 'none',
              }}>
              {s.emoji} {s.name}
            </div>
          ))}
        </div>
      </section>


      {/* === FEATURES === */}
      <section className="py-10 md:py-16 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-sm font-bold uppercase tracking-widest mb-3" style={{ color: '#6366f1' }}>Everything You Need</p>
            <h2 className="font-black mb-4" style={{ fontSize: 'clamp(2rem,5vw,3.5rem)' }}>
              One Platform. <span className="gradient-text">Infinite Games.</span>
            </h2>
            <p className={`${theme.textMuted} text-lg max-w-xl mx-auto`}>From your first pickup game to championship glory — Kynaix Sports has every tool you need.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((f) => (
              <div key={f.title} className="card-hover rounded-2xl p-6"
                style={{ background: cardBg, border: `1px solid ${cardBorder}`, cursor: 'default' }}>
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                  style={{ background: f.color + '18', border: `1px solid ${f.color}40` }}>
                  <f.icon size={22} style={{ color: f.color }} />
                </div>
                <h3 className={`font-bold text-lg mb-2 ${theme.text}`}>{f.title}</h3>
                <p className={`${theme.textMuted} text-sm leading-relaxed`}>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* === HOW IT WORKS === */}
      <section className="py-12 md:py-24 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-sm font-bold uppercase tracking-widest mb-3" style={{ color: '#a855f7' }}>Simple to Start</p>
          <h2 className="font-black mb-16" style={{ fontSize: 'clamp(2rem,5vw,3rem)' }}>Up &amp; Running in <span className="gradient-text">3 Steps</span></h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: '01', icon: '🎯', title: 'Create Account', desc: 'Sign up free. Set your sports, location, and skill level in minutes.' },
              { step: '02', icon: '🔍', title: 'Find Your Game', desc: 'Browse tournaments, join squads, or challenge players near you.' },
              { step: '03', icon: '🏆', title: 'Compete & Win', desc: 'Play, score live, climb the leaderboard, and claim your glory.' },
            ].map((s) => (
              <div key={s.step} className="relative">
                <div className="text-7xl font-black mb-4" style={{ color: isDark ? 'rgba(99,102,241,0.4)' : 'rgba(99,102,241,0.2)', lineHeight: 1 }}>{s.step}</div>
                <div className="text-4xl mb-4">{s.icon}</div>
                <h3 className={`font-bold text-xl mb-2 ${theme.text}`}>{s.title}</h3>
                <p className={theme.textMuted}>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* === SQUADS SHOWCASE === */}
      <section className="py-12 md:py-24 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <div className="rounded-3xl overflow-hidden" style={{ background: 'linear-gradient(135deg, rgba(168,85,247,0.12) 0%, rgba(59,130,246,0.12) 100%)', border: `1px solid ${overlayBorder}` }}>
            <div className="grid md:grid-cols-2 gap-0">
              <div className="p-6 md:p-10 lg:p-16 flex flex-col justify-center">
                <div className="text-4xl mb-4">👥</div>
                <h2 className="font-black text-2xl md:text-4xl mb-4">Build Your <span className="gradient-text">Dream Squad</span></h2>
                <p className={`${theme.textSecondary} text-base md:text-lg mb-6 leading-relaxed`}>
                  Your squad is more than a team. It's your sports family. Chat in real-time, track shared expenses, schedule games, and celebrate wins together.
                </p>
                <ul className="space-y-3 mb-6">
                  {['Group chat & messaging', 'Expense tracking', 'Game scheduling', 'Member management', 'Private & public squads'].map(item => (
                    <li key={item} className={`flex items-center gap-3 ${theme.textSecondary}`}>
                      <span className="w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0" style={{ background: '#a855f7' }}>✓</span>
                      {item}
                    </li>
                  ))}
                </ul>
                <button onClick={() => navigate(isAuthenticated ? '/squads' : '/signup')}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-bold w-fit"
                  style={{ background: '#a855f7', color: '#fff' }}>
                  Create Your Squad <ChevronRight size={16} />
                </button>
              </div>
              <div className="p-4 sm:p-6 flex items-center justify-center">
                {/* Squad card mockup */}
                <div className="w-full rounded-2xl p-4 space-y-3" style={{ background: overlayBg, border: `1px solid ${overlayBorder}` }}>
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center text-xl flex-shrink-0" style={{ background: 'rgba(168,85,247,0.2)' }}>🏀</div>
                    <div className="flex-1 min-w-0">
                      <div className={`font-bold truncate ${theme.text}`}>Midnight Ballers</div>
                      <div className={`${theme.textMuted} text-sm`}>12 members · Basketball</div>
                    </div>
                    <div className="flex-shrink-0 px-2 py-1 rounded-full text-xs font-bold" style={{ background: 'rgba(34,197,94,0.2)', color: '#4ade80' }}>Public</div>
                  </div>
                  <div className="h-px" style={{ background: cardBorder }} />
                  <div className="flex flex-wrap gap-2">
                    {['Posts', 'Chat', 'Schedule', 'Members'].map(tab => (
                      <span key={tab} className="text-xs px-2 py-1 rounded-full" style={{ background: 'rgba(99,102,241,0.2)', color: '#a5b4fc' }}>{tab}</span>
                    ))}
                  </div>
                  <div className="space-y-2">
                    {['Alex M. scored 28 pts 🔥', 'Game tonight at 8pm 📅', 'New tournament starting!'].map(post => (
                      <div key={post} className={`text-sm ${theme.textSecondary} py-2 px-3 rounded-lg`} style={{ background: cardBg }}>{post}</div>
                    ))}
                  </div>
                  <div className="flex -gap-x-2">
                    {['🧑', '👩', '🧑‍🦱', '👨‍🦰', '🧑‍🦲'].map((avatar, i) => (
                      <div key={i} className="w-8 h-8 rounded-full flex items-center justify-center text-sm border-2" style={{ borderColor: '#050510', background: 'rgba(99,102,241,0.3)' }}>{avatar}</div>
                    ))}
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold border-2" style={{ borderColor: '#050510', background: 'rgba(168,85,247,0.3)', color: '#e9d5ff' }}>+7</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* === MARKETPLACE SHOWCASE === */}
      <section className="py-12 md:py-24 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <div className="rounded-3xl overflow-hidden" style={{ background: 'linear-gradient(135deg, rgba(244,63,94,0.1) 0%, rgba(249,115,22,0.1) 100%)', border: `1px solid ${overlayBorder}` }}>
            <div className="grid md:grid-cols-2 gap-0">
              {/* Right: mockup first on desktop (reversed) */}
              <div className="p-4 sm:p-6 flex items-center justify-center order-2 md:order-1">
                <div className="w-full space-y-3">
                  {/* Marketplace product cards mockup */}
                  {[
                    { emoji: '🎾', name: 'Wilson Pro Tennis Racket', price: '$89', condition: 'Like New', seller: 'Sarah K.' },
                    { emoji: '🏀', name: 'Nike Basketball Shoes sz 11', price: '$55', condition: 'Good', seller: 'Mike T.' },
                    { emoji: '⚽', name: 'Adidas Match Ball (Set of 3)', price: '$45', condition: 'New', seller: 'Jordan M.' },
                  ].map((item) => (
                    <div key={item.name} className="flex items-center gap-2 rounded-xl p-3" style={{ background: overlayBg, border: `1px solid ${cardBorder}` }}>
                      <div className="w-9 h-9 rounded-lg flex items-center justify-center text-lg flex-shrink-0" style={{ background: 'rgba(244,63,94,0.15)' }}>{item.emoji}</div>
                      <div className="flex-1 min-w-0">
                        <div className={`font-semibold text-sm ${theme.text} truncate`}>{item.name}</div>
                        <div className={`text-xs mt-0.5 ${theme.textMuted} truncate`}>by {item.seller} · {item.condition}</div>
                      </div>
                      <div className="font-black text-base flex-shrink-0 ml-1" style={{ color: '#f43f5e' }}>{item.price}</div>
                    </div>
                  ))}
                  {/* Stats bar */}
                  <div className="grid grid-cols-3 gap-2 pt-2">
                    {[{ n: '2.4K', l: 'Listings' }, { n: '$12', l: 'Avg Deal' }, { n: '4.9★', l: 'Seller Rating' }].map(s => (
                      <div key={s.l} className="text-center rounded-xl py-3" style={{ background: cardBg, border: `1px solid ${cardBorder}` }}>
                        <div className="font-black text-sm" style={{ color: '#f43f5e' }}>{s.n}</div>
                        <div className={`text-xs ${theme.textMuted}`}>{s.l}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              {/* Left: copy */}
              <div className="p-6 md:p-10 lg:p-16 flex flex-col justify-center order-1 md:order-2">
                <div className="text-4xl mb-4">🛒</div>
                <h2 className="font-black text-2xl md:text-3xl lg:text-4xl mb-4">Buy &amp; Sell <span className="gradient-text">Sports Gear</span></h2>
                <p className={`${theme.textSecondary} text-lg mb-8 leading-relaxed`}>
                  Your community's marketplace for sports equipment. Buy gear from athletes near you, or sell what you no longer use — safe, simple, and built for the sports community.
                </p>
                <ul className="space-y-3 mb-8">
                  {[
                    'Buy & sell sports gear locally',
                    'List equipment in under 2 minutes',
                    'Trusted community sellers',
                    'Sports-specific categories',
                    'Save & watch your favourite listings',
                  ].map(item => (
                    <li key={item} className={`flex items-center gap-3 ${theme.textSecondary}`}>
                      <span className="w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0" style={{ background: '#f43f5e' }}>✓</span>
                      {item}
                    </li>
                  ))}
                </ul>
                <button onClick={() => navigate(isAuthenticated ? '/marketplace' : '/signin')}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-bold w-fit"
                  style={{ background: '#f43f5e', color: '#fff' }}>
                  Browse Marketplace <ChevronRight size={16} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* === COMING SOON === */}
      <section className="py-12 md:py-24 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-5 text-sm font-bold"
              style={{ background: 'rgba(234,179,8,0.1)', border: '1px solid rgba(234,179,8,0.35)', color: '#fbbf24' }}>
              🚀 &nbsp;Coming Soon
            </div>
            <h2 className="font-black mb-4" style={{ fontSize: 'clamp(2rem,5vw,3.5rem)' }}>
              The Future of <span className="gradient-text">Sports Is Here</span>
            </h2>
            <p className={`${theme.textMuted} text-lg max-w-xl mx-auto`}>We're building the complete sports ecosystem. Here's what's coming next.</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">

            {/* Card 1: One-Stop Shop */}
            <div className="rounded-3xl p-8 relative overflow-hidden"
              style={{ background: cardBg, border: '1px dashed rgba(234,179,8,0.35)' }}>
              {/* Coming soon badge */}
              <div className="absolute top-5 right-5 px-3 py-1 rounded-full text-xs font-black uppercase tracking-widest"
                style={{ background: 'rgba(234,179,8,0.15)', color: '#fbbf24', border: '1px solid rgba(234,179,8,0.3)' }}>
                Q3 2026
              </div>
              <div className="text-5xl mb-5">🏪</div>
              <h3 className="font-black text-2xl mb-3">One-Stop Shop for Sporting Needs</h3>
              <p className={`${theme.textMuted} leading-relaxed mb-6`}>
                Everything a sports person needs — equipment, apparel, nutrition, coaching tools, and event registrations — all discoverable in one unified store. No more hopping between apps.
              </p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { icon: '⚙️', label: 'Equipment & Gear' },
                  { icon: '👟', label: 'Sports Apparel' },
                  { icon: '🥤', label: 'Nutrition & Supplements' },
                  { icon: '🎟️', label: 'Event Tickets & Registration' },
                  { icon: '📹', label: 'Training Videos' },
                  { icon: '🏟️', label: 'Venue Booking' },
                ].map(item => (
                  <div key={item.label} className="flex items-center gap-2 rounded-xl px-3 py-2.5 text-sm"
                    style={{ background: 'rgba(234,179,8,0.06)', border: '1px solid rgba(234,179,8,0.12)' }}>
                    <span>{item.icon}</span>
                    <span className={`${theme.textSecondary} font-medium`}>{item.label}</span>
                  </div>
                ))}
              </div>
              {/* Progress bar */}
              <div className="mt-6">
                <div className={`flex justify-between text-xs mb-2 ${theme.textMuted}`}>
                  <span>Development Progress</span>
                  <span style={{ color: '#fbbf24' }}>40%</span>
                </div>
                <div className="h-1.5 rounded-full" style={{ background: cardBorder }}>
                  <div className="h-1.5 rounded-full transition-all" style={{ width: '40%', background: 'linear-gradient(90deg, #fbbf24, #f97316)' }} />
                </div>
              </div>
            </div>

            {/* Card 2: Academies / Schools / Affiliates */}
            <div className="rounded-3xl p-8 relative overflow-hidden"
              style={{ background: cardBg, border: '1px dashed rgba(34,197,94,0.35)' }}>
              <div className="absolute top-5 right-5 px-3 py-1 rounded-full text-xs font-black uppercase tracking-widest"
                style={{ background: 'rgba(34,197,94,0.12)', color: '#4ade80', border: '1px solid rgba(34,197,94,0.3)' }}>
                Q4 2026
              </div>
              <div className="text-5xl mb-5">🎓</div>
              <h3 className="font-black text-2xl mb-3">Academies, Schools &amp; Affiliates</h3>
              <p className={`${theme.textMuted} leading-relaxed mb-6`}>
                A dedicated hub for sports academies, school programs, and club affiliates to manage rosters, publish coaching programs, run inter-school tournaments, and grow their athlete base.
              </p>
              <div className="space-y-3">
                {[
                  { icon: '🏫', title: 'School Sports Programs', desc: 'Manage inter-school leagues, rosters & results' },
                  { icon: '🏅', title: 'Coaching Academies', desc: 'Publish programs, enroll athletes, track progress' },
                  { icon: '🤝', title: 'Club Affiliates', desc: 'Partner with local clubs & share player pipelines' },
                  { icon: '📊', title: 'Talent Discovery', desc: 'Scouts & coaches find rising athletes by stats' },
                ].map(item => (
                  <div key={item.title} className="flex items-start gap-3 rounded-xl px-4 py-3"
                    style={{ background: 'rgba(34,197,94,0.05)', border: '1px solid rgba(34,197,94,0.12)' }}>
                    <span className="text-xl flex-shrink-0 mt-0.5">{item.icon}</span>
                    <div>
                      <div className={`font-bold text-sm ${theme.text}`}>{item.title}</div>
                      <div className={`text-xs mt-0.5 ${theme.textMuted}`}>{item.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
              {/* Progress bar */}
              <div className="mt-6">
                <div className={`flex justify-between text-xs mb-2 ${theme.textMuted}`}>
                  <span>Development Progress</span>
                  <span style={{ color: '#4ade80' }}>15%</span>
                </div>
                <div className="h-1.5 rounded-full" style={{ background: cardBorder }}>
                  <div className="h-1.5 rounded-full transition-all" style={{ width: '15%', background: 'linear-gradient(90deg, #22c55e, #10b981)' }} />
                </div>
              </div>
            </div>
          </div>

          {/* Notify me strip — only for guests */}
          {!isAuthenticated && (
            <div className="mt-10 rounded-2xl px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-4"
              style={{ background: cardBg, border: `1px solid ${cardBorder}` }}>
              <div>
                <div className={`font-bold ${theme.text} text-lg`}>Want early access?</div>
                <div className={`${theme.textMuted} text-sm mt-0.5`}>Create your free account and we'll notify you when these features launch.</div>
              </div>
              <button
                onClick={() => navigate('/signup')}
                className="flex-shrink-0 px-6 py-3 rounded-xl font-bold text-sm"
                style={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', color: '#fff', whiteSpace: 'nowrap' }}>
                Get Early Access →
              </button>
            </div>
          )}
        </div>
      </section>

      {/* === CTA FINAL === */}
      <section className="py-12 md:py-24 px-4 sm:px-6 text-center">
        <div className="max-w-3xl mx-auto">
          <div className="text-6xl mb-6">🏆</div>
          <h2 className="font-black mb-6" style={{ fontSize: 'clamp(2.5rem,6vw,4rem)' }}>
            Ready to <span className="gradient-text">Compete?</span>
          </h2>
          <p className={`${theme.textMuted} text-xl mb-10`}>
            Join thousands of athletes already on Kynaix Sports. Your first tournament is one click away.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {isAuthenticated ? (
              <>
                <button
                  onClick={() => navigate('/tournaments')}
                  className="px-12 py-5 rounded-2xl font-black text-xl transition-all"
                  style={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', color: '#fff', boxShadow: '0 0 60px rgba(99,102,241,0.5)' }}>
                  Explore Tournaments
                </button>
                <button
                  onClick={() => navigate('/social-feed')}
                  className="px-12 py-5 rounded-2xl font-bold text-xl text-white"
                  style={{ background: 'linear-gradient(135deg, #ec4899, #f97316)', boxShadow: '0 0 60px rgba(236,72,153,0.4)' }}>
                  Browse Feed
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => navigate('/signup')}
                  className="px-12 py-5 rounded-2xl font-black text-xl transition-all"
                  style={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', color: '#fff', boxShadow: '0 0 60px rgba(99,102,241,0.5)' }}>
                  Join Free Today
                </button>
                <button
                  onClick={() => navigate('/signin')}
                  className="px-12 py-5 rounded-2xl font-bold text-xl text-white"
                  style={{ background: 'linear-gradient(135deg, #ec4899, #f97316)', boxShadow: '0 0 60px rgba(236,72,153,0.4)' }}>
                  Sign In
                </button>
              </>
            )}
          </div>
          <p className={`${theme.textMuted} text-sm mt-6`}>No credit card required · Free forever for players</p>
        </div>
      </section>

      {/* === FOOTER === */}
      <footer className="py-10 px-6 text-center" style={{ borderTop: `1px solid ${cardBorder}` }}>
        <div className="text-2xl font-black gradient-text mb-2">Kynaix Sports</div>
        <p className={`${theme.textMuted} text-sm`}>Your Game. Your Squad. Your Glory.</p>
      </footer>

      {/* === GET APP MODAL === */}
      {showGetApp && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(6px)' }}
          onClick={() => setShowGetApp(false)}>
          <div
            className="relative w-full max-w-lg rounded-3xl overflow-hidden"
            style={{ background: '#111127', border: '1px solid rgba(255,255,255,0.1)', boxShadow: '0 0 80px rgba(99,102,241,0.25)' }}
            onClick={e => e.stopPropagation()}>

            {/* Close button */}
            <button
              onClick={() => setShowGetApp(false)}
              className="absolute top-3 right-3 z-10 w-8 h-8 flex items-center justify-center rounded-full text-sm font-bold transition-all"
              style={{ background: 'rgba(255,255,255,0.1)', color: '#9ca3af' }}>
              ✕
            </button>

            {/* Hero image */}
            <img
              src="https://www.kynaixsports.com/images/app_assets/social_previews/social-preview.jpg"
              alt="Kynaix Sports"
              className="w-full block"
              style={{ aspectRatio: '1200/630', objectFit: 'cover' }}
            />

            <div className="p-6 space-y-5">
              {/* Headline */}
              <div>
                <h2 className="font-black text-xl text-white mb-1">Get the app on your phone</h2>
                <p style={{ color: '#9ca3af', fontSize: '0.875rem' }}>
                  Scan the QR code or send the link to yourself.
                </p>
              </div>

              {/* QR + Store row */}
              <div className="flex items-center gap-4">
                <div className="flex-shrink-0 p-2 rounded-xl bg-white">
                  <QRCodeSVG value={APP_URL} size={72} bgColor="#ffffff" fgColor="#1a1a2e" level="M" />
                </div>
                <div className="flex flex-col gap-2 flex-1">
                  <a href="https://apps.apple.com/us/app/kynaix-sports/id6761959059" target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold text-white transition-all"
                    style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)' }}>
                    <span className="text-base">🍎</span>
                    <div><div style={{ fontSize: '0.65rem', color: '#9ca3af' }}>Download on the</div><div style={{ fontSize: '0.85rem' }}>App Store</div></div>
                  </a>
                  <a href="https://play.google.com/store/apps/details?id=com.kynaixsports" target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold text-white transition-all"
                    style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)' }}>
                    <span className="text-base">▶️</span>
                    <div><div style={{ fontSize: '0.65rem', color: '#9ca3af' }}>Get it on</div><div style={{ fontSize: '0.85rem' }}>Google Play</div></div>
                  </a>
                </div>
              </div>

              {/* Divider */}
              <div style={{ height: '1px', background: 'rgba(255,255,255,0.07)' }} />

              {/* Email send */}
              {modalSent ? (
                <div className="flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-semibold"
                  style={{ background: 'rgba(34,197,94,0.12)', border: '1px solid rgba(34,197,94,0.3)', color: '#4ade80' }}>
                  <Check size={15} /> Email sent! Check your inbox.
                </div>
              ) : (
                <div>
                  <p className="text-xs font-bold uppercase tracking-widest mb-2" style={{ color: '#6b7280' }}>📧 Send link to your email</p>
                  <div className="flex gap-2">
                    <input
                      type="email"
                      placeholder="your@email.com"
                      value={modalEmail}
                      onChange={e => setModalEmail(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && handleModalSend()}
                      className="flex-1 rounded-xl px-3 py-2.5 text-sm text-white outline-none"
                      style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)' }}
                    />
                    <button
                      onClick={handleModalSend}
                      disabled={modalSending}
                      className="px-4 py-2.5 rounded-xl text-sm font-bold text-white transition-all"
                      style={{ background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', opacity: modalSending ? 0.6 : 1 }}>
                      {modalSending ? '…' : 'Send'}
                    </button>
                  </div>
                </div>
              )}

              {/* Copy link */}
              <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl"
                style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <span className="flex-1 text-xs truncate" style={{ color: '#9ca3af' }}>{APP_URL}</span>
                <button
                  onClick={handleModalCopy}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all"
                  style={{
                    background: modalCopied ? 'rgba(34,197,94,0.2)' : 'rgba(99,102,241,0.2)',
                    border: `1px solid ${modalCopied ? 'rgba(34,197,94,0.4)' : 'rgba(99,102,241,0.35)'}`,
                    color: modalCopied ? '#4ade80' : '#a5b4fc',
                  }}>
                  {modalCopied ? <Check size={12} /> : <Copy size={12} />}
                  {modalCopied ? 'Copied!' : 'Copy'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
